// Settle up between partners (piece 3 of partner splits): the balance maths, grouping and every
// line of settle copy. Shared by the Worker (src/db/settle-repo.ts, src/routes/settle.ts) and the
// PWA (web/src/views/settle.ts), so both always agree on the numbers. Pure: no I/O, no clock.
//
// A line is one accepted split request between the two households. Its amount is the share the
// one who did not pay accepted (split_requests.recipient_share_cents), signed from my side:
// positive when I paid (my partner owes me), negative when my partner paid (I owe). Nothing is
// rounded here: the shares were rounded once, when the split was made (splitShares()).

/** A line on the Splits screen, from my side. */
export interface SettleLine {
  /** The split request's id. */
  id: string;
  /** My purchase's date (YYYY-MM-DD), else the day it was asked. */
  date: string;
  merchant: string;
  /** My own category for my side of it (never the partner's). Null: no category. */
  categoryName: string | null;
  /** Signed: + my partner owes me, - I owe my partner. Never 0. */
  amountCents: number;
}

export interface Balance {
  /** Signed: + my partner owes me, - I owe. */
  netCents: number;
  /** What my partner owes me (lines I paid), 0 or more. */
  owedToMeCents: number;
  /** What I owe my partner (lines they paid), 0 or more. */
  iOweCents: number;
}

export function balanceOf(lines: Pick<SettleLine, 'amountCents'>[]): Balance {
  let owedToMeCents = 0;
  let iOweCents = 0;
  for (const l of lines) {
    if (!Number.isInteger(l.amountCents)) throw new Error('A line amount must be whole cents.');
    if (l.amountCents > 0) owedToMeCents += l.amountCents;
    else iOweCents -= l.amountCents;
  }
  return { netCents: owedToMeCents - iOweCents, owedToMeCents, iOweCents };
}

/** The group name for lines with no category of mine. */
export const NO_CATEGORY = 'No category';

export interface SettleGroup<L extends SettleLine = SettleLine> {
  categoryName: string;
  subtotalCents: number;
  lines: L[];
}

/** Lines grouped by my own category (name order, no category last), newest first in each, with a subtotal. */
export function groupLines<L extends SettleLine>(lines: L[]): SettleGroup<L>[] {
  const groups = new Map<string, L[]>();
  for (const l of lines) {
    const key = l.categoryName ?? NO_CATEGORY;
    groups.set(key, [...(groups.get(key) ?? []), l]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a === NO_CATEGORY ? 1 : b === NO_CATEGORY ? -1 : a.localeCompare(b)))
    .map(([categoryName, ls]) => ({
      categoryName,
      subtotalCents: balanceOf(ls).netCents,
      lines: [...ls].sort((x, y) => (x.date === y.date ? x.id.localeCompare(y.id) : y.date.localeCompare(x.date))),
    }));
}

/** The lines of a new settlement as stored: signed from the proposer's side. */
export interface SettlementLineInput {
  splitRequestId: string;
  direction: 'requester_paid' | 'recipient_paid';
  amountCents: number;
}

/**
 * The settlement for the ticked lines, from the proposer's side: each line's direction and
 * frozen amount, and the net (always the signed sum of the lines). Null with no lines.
 */
export function settlementFor(lines: Pick<SettleLine, 'id' | 'amountCents'>[]): { netCents: number; lines: SettlementLineInput[] } | null {
  if (!lines.length) return null;
  const out = lines.map((l) => ({
    splitRequestId: l.id,
    direction: l.amountCents > 0 ? ('requester_paid' as const) : ('recipient_paid' as const),
    amountCents: l.amountCents,
  }));
  return { netCents: netOf(out), lines: out };
}

/** The signed sum of a settlement's lines. A settlement's net_cents must equal it. */
export const netOf = (lines: { amountCents: number }[]) => balanceOf(lines).netCents;

/** Who pays, from my side: + my partner owes me (they pay), - I pay, 0 nobody. */
export const payerOf = (netCents: number): 'me' | 'partner' | null => (netCents > 0 ? 'partner' : netCents < 0 ? 'me' : null);

/**
 * Is `day` (YYYY-MM-DD, the household's local date) a reminder day? The last day of a month and
 * the 1st of the next share one key, the month that is ending, so only one reminder is sent.
 */
export function reminderMonth(day: string): string | null {
  const [y, m, d] = day.split('-').map(Number);
  if (d === 1) return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return d === last ? day.slice(0, 7) : null;
}

const dollars = (cents: number) => `$${(Math.abs(cents) / 100).toFixed(2)}`;

/** The top of the Splits screen: "Tiffany owes you $84.20". */
export function netLine(partner: string, netCents: number): string {
  if (netCents > 0) return `${partner} owes you ${dollars(netCents)}`;
  if (netCents < 0) return `You owe ${partner} ${dollars(netCents)}`;
  return `You and ${partner} are all square`;
}

/** The Settle up sheet: who pays whom for the ticked lines. */
export function settleSummary(partner: string, netCents: number, count: number): string {
  const n = `${count} split${count === 1 ? '' : 's'}`;
  if (netCents > 0) return `${partner} pays you ${dollars(netCents)} for ${n}.`;
  if (netCents < 0) return `You pay ${partner} ${dollars(netCents)} for ${n}.`;
  return `These ${n} even out. Nobody pays.`;
}

/**
 * The Settle up button: the same action, worded by the net of the ticked lines (from my side).
 * My partner owes me: "Mark as paid by Tiffany". I owe: "I paid Tiffany". Even: "Mark as settled".
 */
export function settleButton(partner: string, netCents: number): string {
  if (netCents > 0) return `Mark as paid by ${partner}`;
  if (netCents < 0) return `I paid ${partner}`;
  return 'Mark as settled';
}

/** How long the other person can dispute a settle up. */
export const DISPUTE_DAYS = 7;

/** When a settle up made at `createdAt` can no longer be disputed. */
export const disputeUntil = (createdAt: string) => new Date(Date.parse(createdAt) + DISPUTE_DAYS * 86_400_000).toISOString();

/** Can the other person still dispute it? Once only, within DISPUTE_DAYS. */
export const canDispute = (s: { status: string; disputedAt: string | null; createdAt: string }, now: Date) =>
  s.status === 'confirmed' && !s.disputedAt && now.getTime() < Date.parse(disputeUntil(s.createdAt));

/**
 * The notice the other person gets on Overview: "Neil marked $84.20 as paid". `netCents` is from
 * their side (+ the marker owed them and paid).
 */
export function markedNotice(marker: string, netCents: number): { line: string; who: string } {
  if (netCents > 0) return { line: `${marker} marked ${dollars(netCents)} as paid`, who: `${marker} paid you.` };
  if (netCents < 0) return { line: `${marker} marked ${dollars(netCents)} as paid`, who: `You paid ${marker}.` };
  return { line: `${marker} marked your splits as settled`, who: 'They even out.' };
}

/** What the one who marked it sees after a dispute. */
export const disputedNotice = (partner: string) => `${partner} disputed your settle up. Those splits are open again.`;

export type SettlementStatus = 'pending' | 'confirmed' | 'declined' | 'cancelled';
export type SettlementEnded = 'requester' | 'split_changed' | 'unlinked' | 'account';
/** A settle up as people see it: settled at once, disputed, or (older ones) ended another way. */
export type SettlementView = 'settled' | 'disputed' | 'declined' | 'cancelled';

export const settlementView = (s: { status: SettlementStatus; disputedAt: string | null }): SettlementView =>
  s.status === 'confirmed' ? (s.disputedAt ? 'disputed' : 'settled') : s.status === 'pending' ? 'cancelled' : s.status;

/** A history line's status. `mine`: I marked it. */
export function settlementStatusLine(
  partner: string,
  s: { status: SettlementStatus; endedReason: SettlementEnded | null; endedNote: string | null; disputedAt: string | null; mine: boolean },
): string {
  const who = s.mine ? 'you' : partner;
  switch (settlementView(s)) {
    case 'settled':
      return `Marked as settled by ${who}`;
    case 'disputed':
      return `Disputed by ${s.mine ? partner : 'you'}`;
    case 'declined':
      return s.mine ? `${partner} declined` : 'You declined';
    case 'cancelled':
      if (s.endedNote === 'one_step') return 'Cancelled when settling became one step';
      return SETTLEMENT_CANCELLED[s.endedReason ?? 'requester'](partner, s.mine);
  }
}

export const SETTLEMENT_CANCELLED: Record<SettlementEnded, (partner: string, mine: boolean) => string> = {
  requester: (partner, mine) => (mine ? 'You cancelled it' : `${partner} cancelled it`),
  split_changed: () => 'Cancelled because a split in it changed',
  unlinked: () => 'Cancelled because you are no longer linked',
  account: () => 'Cancelled because an account was deleted',
};

// ---- History (the Splits screen) ------------------------------------------------------------

/** History shows this many settle ups; "Show older" reveals the rest. */
export const HISTORY_SHOWN = 5;

export interface HistoryGroup<L extends SettleLine = SettleLine> {
  /** Newest purchase first. */
  lines: L[];
  /** The group's total, 0 or more (the group's title says who owed whom). */
  totalCents: number;
}

/**
 * One settle up's lines, split by direction: what my partner owed me (+ lines) and what I owed my
 * partner (- lines), each newest first with its subtotal. `netCents` is owed-to-me minus I-owed:
 * the same signed net the settle up was made with (`settlements.net_cents` from my side).
 */
export function historyBreakdown<L extends SettleLine>(lines: readonly L[]): { owedToMe: HistoryGroup<L>; iOwed: HistoryGroup<L>; netCents: number } {
  const newest = [...lines].sort((a, b) => b.date.localeCompare(a.date));
  const owed = newest.filter((l) => l.amountCents > 0);
  const owe = newest.filter((l) => l.amountCents < 0);
  const b = balanceOf(newest);
  return { owedToMe: { lines: owed, totalCents: b.owedToMeCents }, iOwed: { lines: owe, totalCents: b.iOweCents }, netCents: b.netCents };
}

/**
 * Who paid whom, in words, from the settle up's own signed net (from my side: + my partner paid
 * me). A settle up that stands says "paid"; a disputed or ended one says who owed whom, because no
 * payment stands. Never a bare amount.
 */
export function historyNetText(partner: string, s: { view: SettlementView; netCents: number }): string {
  const paid = s.view === 'settled';
  if (s.netCents > 0) return paid ? `${partner} paid you ${dollars(s.netCents)}` : `${partner} owed you ${dollars(s.netCents)}`;
  if (s.netCents < 0) return paid ? `You paid ${partner} ${dollars(s.netCents)}` : `You owed ${partner} ${dollars(s.netCents)}`;
  return 'All square, nothing to pay';
}

/** "9 purchases". */
export const purchasesText = (n: number) => `${n} purchase${n === 1 ? '' : 's'}`;
/** The expanded groups' titles: "Tiffany owed you $52.10 (9)", "You owed Tiffany $97.60 (10)". */
export const OWED_TO_ME_TITLE = (partner: string, cents: number, n: number) => `${partner} owed you ${dollars(cents)} (${n})`;
export const I_OWED_TITLE = (partner: string, cents: number, n: number) => `You owed ${partner} ${dollars(cents)} (${n})`;
/** The net line under the groups: the collapsed row's words, after "Net:". */
export const historyNetLine = (partner: string, s: { view: SettlementView; netCents: number }) => `Net: ${historyNetText(partner, s)}`;
export const SHOW_OLDER = (n: number) => `Show older (${n})`;

/** Refusals. */
export const SETTLE_CHANGED = 'The balance changed. Open Splits again and check the lines.';
export const SETTLE_LINE_TAKEN = 'A line is already in a settlement. Open Splits again.';
export const SETTLE_NOT_DISPUTABLE = 'This settle up can no longer be disputed.';
export const SETTLE_NEEDS_LINK = 'Settling needs an active partner link. The balance stays here to see.';

/** The Overview card on the last day and the 1st, while something is outstanding. */
export const REMINDER_TITLE = 'Splits to settle';

/** The month-end email: plain text, no amounts and no merchant names, a link to the app only. */
export const settleReminderEmail = (partner: string, appUrl: string) => ({
  subject: 'Splits to settle in Hush',
  text: `You and ${partner} have splits to settle. Open Hush to see them and settle up:\n\n${appUrl}\n\nYou get this email at the end of a month while splits are outstanding. Unlink in Settings, Partner.`,
});

/** Settle up emails: plain text, no amounts or merchant names, a link to the app only. */
export const settleMarkedEmail = (from: string, appUrl: string) => ({
  subject: `${from} marked splits as settled in Hush`,
  text: `${from} marked some of your splits as settled. Open Hush to see them. If something is wrong, you can dispute it for ${DISPUTE_DAYS} days:\n\n${appUrl}`,
});
export const settleDisputedEmail = (from: string, appUrl: string) => ({
  subject: `${from} disputed a settle up in Hush`,
  text: `${from} disputed a settle up you marked. Those splits are open again. Open Hush to see them:\n\n${appUrl}`,
});
